// Recents and favorites (step B1 of the redesign, REDISENO_UI.md 3.1 and
// 3.2): every resource a user opens lands in "Recientes", and the star of the
// resource header keeps it in "Favoritos". Inicio, the sidebar and the search
// read them (later page PRs); this module only keeps the list.
//
// Kept in this browser, one list per tenant and user
// (nexus.recents.<tenant>.<user>), like pinnedAppsStore: it survives a reload
// without a backend round-trip, and switching tenant or account never shows
// another one's list. A resource is identified by (kind, id).
import { create } from 'zustand';
import { useAuthStore, getTenantId } from '../store/authStore';
import type { ResourceKind } from '../design-system/resourceIcons';
import { resourceHref } from './resourceRoutes';

export const MAX_RECENTS = 20;
export const MAX_FAVORITES = 100;
const KEY_PREFIX = 'nexus.recents.';

/** Enough to draw a resource in a list and open it again. */
export interface ResourceRef {
  kind: ResourceKind;
  id: string;
  name: string;
  /** Shell page id that opens it (pipelines, ontology...). */
  page: string;
  /** Tab it was open on, when the page has tabs. */
  tab?: string | null;
  /** Object types and records carry their own color. */
  color?: string;
}

export interface RecentEntry extends ResourceRef { openedAt: number }
export interface FavoriteEntry extends ResourceRef { addedAt: number }

interface Stored { recents: RecentEntry[]; favorites: FavoriteEntry[] }

const same = (a: { kind: string; id: string }, b: { kind: string; id: string }) => a.kind === b.kind && a.id === b.id;

function isRef(x: unknown): x is ResourceRef {
  const r = x as ResourceRef;
  return !!r && typeof r.kind === 'string' && typeof r.id === 'string' && typeof r.name === 'string' && typeof r.page === 'string';
}

function read(key: string): Stored {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { recents: [], favorites: [] };
    const parsed = JSON.parse(raw);
    return {
      recents: (Array.isArray(parsed.recents) ? parsed.recents : []).filter(isRef).slice(0, MAX_RECENTS),
      favorites: (Array.isArray(parsed.favorites) ? parsed.favorites : []).filter(isRef).slice(0, MAX_FAVORITES),
    };
  } catch {
    return { recents: [], favorites: [] };
  }
}

function write(key: string, data: Stored) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch { /* storage full or blocked: the list lasts this page load */ }
}

/** Storage key of the signed-in user in the active tenant. */
export function recentsScope(): string {
  const user = useAuthStore.getState().user;
  return `${KEY_PREFIX}${getTenantId()}.${user?.id ?? 'anon'}`;
}

function clean(ref: ResourceRef): ResourceRef {
  const out: ResourceRef = { kind: ref.kind, id: ref.id, name: ref.name, page: ref.page };
  if (ref.tab) out.tab = ref.tab;
  if (ref.color) out.color = ref.color;
  return out;
}

interface RecentsState extends Stored {
  /** Key the lists in memory belong to. */
  scope: string | null;
  /** Re-read the lists when the tenant or user changed. */
  ensureScope: () => void;
  /** A resource was opened: move it to the top of Recientes. */
  recordVisit: (ref: ResourceRef, at?: number) => void;
  /** Update the name (or color) shown for a resource, wherever it is listed. */
  rename: (kind: ResourceKind, id: string, name: string) => void;
  /** Drop a resource from both lists (it was deleted). */
  forget: (kind: ResourceKind, id: string) => void;
  clearRecents: () => void;
  isFavorite: (kind: ResourceKind, id: string) => boolean;
  toggleFavorite: (ref: ResourceRef, at?: number) => boolean;
}

export const useRecentsStore = create<RecentsState>((set, get) => {
  const commit = (next: Partial<Stored>) => {
    const s = get();
    const data: Stored = { recents: next.recents ?? s.recents, favorites: next.favorites ?? s.favorites };
    set(data);
    if (s.scope) write(s.scope, data);
  };
  return {
    scope: null,
    recents: [],
    favorites: [],

    ensureScope: () => {
      const scope = recentsScope();
      if (get().scope === scope) return;
      set({ scope, ...read(scope) });
    },

    recordVisit: (ref, at = Date.now()) => {
      get().ensureScope();
      if (!ref.id || !ref.name) return;
      const entry: RecentEntry = { ...clean(ref), openedAt: at };
      const recents = [entry, ...get().recents.filter((r) => !same(r, ref))].slice(0, MAX_RECENTS);
      // The favorite keeps the current name too.
      const favorites = get().favorites.map((f) => (same(f, ref) && f.name !== ref.name ? { ...f, name: ref.name } : f));
      commit({ recents, favorites });
    },

    rename: (kind, id, name) => {
      get().ensureScope();
      const key = { kind, id };
      commit({
        recents: get().recents.map((r) => (same(r, key) ? { ...r, name } : r)),
        favorites: get().favorites.map((f) => (same(f, key) ? { ...f, name } : f)),
      });
    },

    forget: (kind, id) => {
      get().ensureScope();
      const key = { kind, id };
      commit({ recents: get().recents.filter((r) => !same(r, key)), favorites: get().favorites.filter((f) => !same(f, key)) });
    },

    clearRecents: () => {
      get().ensureScope();
      commit({ recents: [] });
    },

    isFavorite: (kind, id) => {
      get().ensureScope();
      return get().favorites.some((f) => same(f, { kind, id }));
    },

    toggleFavorite: (ref, at = Date.now()) => {
      get().ensureScope();
      const favorites = get().favorites;
      if (favorites.some((f) => same(f, ref))) {
        commit({ favorites: favorites.filter((f) => !same(f, ref)) });
        return false;
      }
      commit({ favorites: [{ ...clean(ref), addedAt: at }, ...favorites].slice(0, MAX_FAVORITES) });
      return true;
    },
  };
});

/** Address that reopens a listed resource (on its tab, if it had one). */
export function hrefForRef(ref: ResourceRef): string {
  return resourceHref(ref.page, ref.id, { tab: ref.tab ?? null });
}

// Follow sign-in, sign-out, impersonation and tenant switches: the lists in
// memory always belong to whoever is signed in now.
useAuthStore.subscribe(() => useRecentsStore.getState().ensureScope());
useRecentsStore.getState().ensureScope();

/** Recientes of the signed-in user, newest first. */
export function useRecents(limit = MAX_RECENTS): RecentEntry[] {
  const recents = useRecentsStore((s) => s.recents);
  return recents.slice(0, limit);
}

/** Favoritos of the signed-in user, last starred first. */
export function useFavorites(): FavoriteEntry[] {
  return useRecentsStore((s) => s.favorites);
}

/** Whether a resource is starred, reactive. */
export function useIsFavorite(kind: ResourceKind, id: string | null | undefined): boolean {
  return useRecentsStore((s) => !!id && s.favorites.some((f) => f.kind === kind && f.id === id));
}
